import { useMemo } from 'react';
import useAppStore from '../store/index'; 
import { applyUnproductiveSplitToPersonalLists, resolvePersonnelCostMetrics } from '../utils/personnelCostAdapter';
import { resolveActiveCountry } from './slices/settingsSlice';

export const usePersonalDataSelector = () => {
    const clientData = useAppStore((state: any) => state.clientData);
    const settings = useAppStore((state: any) => state.settings);

    return useMemo(() => {
        const rawPData = clientData?.personalData || [];
        const rawUData = clientData?.unproductivePersonalData || [];
        const { effectivePersonalData: pData, effectiveUnproductiveData: uData } =
            applyUnproductiveSplitToPersonalLists(rawPData, rawUData);
        const aData = clientData?.azubiAssistenzData || [];
        const currentSettings = settings || { global: {}, country: {} };
        const clientLand = clientData?.land || clientData?.Land || currentSettings.selectedCountryId || 'DE';
        const effectiveCountrySettings = resolveActiveCountry(currentSettings, clientData);

        const mapRow = (ma: any, scope: 'produktiv' | 'unproduktiv' | 'azubi') => {
            const wochenstunden = Number(ma.wochenstunden) || 0;
            const wochensatz = currentSettings.global?.Wochensatz || 4.33;
            const monatsstunden = wochenstunden * wochensatz;

            const costMetrics = resolvePersonnelCostMetrics(
                ma,
                {
                    selectedCountryId: currentSettings.selectedCountryId,
                    clientLand,
                    countrySettings: effectiveCountrySettings,
                },
                scope
            );

            const grundgehalt = costMetrics.baseWage;
            const lohnkostenzusatz = costMetrics.lohnzusatz;
            const bonus = scope === 'produktiv' ? costMetrics.bonus : (Number(ma.bonus) || 0);
            const grundlohn = grundgehalt;
            const bruttolohnGesamt = scope === 'produktiv'
                ? grundgehalt + lohnkostenzusatz + bonus
                : grundgehalt + lohnkostenzusatz;

            const stundenlohnGrund = monatsstunden > 0 ? grundgehalt / monatsstunden : 0;
            const stundenlohnGesamt = monatsstunden > 0 ? bruttolohnGesamt / monatsstunden : 0;

            const lohnnebenkostenSatz = costMetrics.lohnnebenkostenSatz;
            const lohnnebenkosten = costMetrics.lohnnebenkosten;
            const totalCost = costMetrics.totalPersonnelCost;

            return {
                ...ma,
                grundgehalt,
                lohnkostenzusatz,
                bonus,
                wochenstunden,
                monatsstunden,
                grundlohn,
                bruttolohnGesamt,
                stundenlohnGrund,
                stundenlohnGesamt,
                lohnnebenkostenSatz,
                lohnnebenkosten,
                totalCost,
                totalPersonnelCost: totalCost,
                agGesamt: totalCost,
                country: costMetrics.isItBz ? 'IT_BZ' : (ma.country || currentSettings.selectedCountryId || 'DE'),
                grossWage: costMetrics.isItBz ? ma.grossWage : grundgehalt,
                netWage: costMetrics.isItBz ? grundgehalt : ma.netWage,
                costoAziendaleOverride: Number(ma.costoAziendaleOverride) || undefined,
            };
        };

        // --- 1. PRODUKTIVE MITARBEITER ---
        const personalData = pData.map((ma: any) => mapRow(ma, 'produktiv'));

        // --- 2. UNPRODUKTIVE MITARBEITER ---
        const unproductivePersonalData = uData.map((ma: any) => mapRow(ma, 'unproduktiv'));
        
        // --- 3. AZUBIS & ASSISTENZ ---
        const azubiAssistenzData = aData.map((ma: any) => mapRow(ma, 'azubi'));

        return {
            personalData,
            unproductivePersonalData,
            azubiAssistenzData,
            leistungsprovision: clientData?.leistungsprovision || {}
        };
    }, [clientData, settings]);
};